<template>
  <div ref="container" class="menu" :class="containerModifierClass" @keydown.esc="onKeyDownEscape">
    <div v-if="!isPermanent" ref="activator" @click.prevent.stop="onActivatorClick">
      <slot name="activator" class="menu-activator" />
    </div>

    <sp-bottom-sheet
      v-if="showBottomSheet"
      :label="label"
      :open="isActive"
      :no-restore-focus="noRestoreFocus"
      :permanent="isPermanent"
      :expand-height-max="mobileExpandHeightMax"
      @close="onSurfaceClosed"
      @click.prevent.stop="onContentClick"
    >
      <slot />
      <slot slot="action" name="sheet-action">
        <sp-button class="bottom-sheet-close-action" color="primary">{{ t("menu_close") }}</sp-button>
      </slot>
    </sp-bottom-sheet>

    <sp-popup
      v-else
      :active="isActive"
      :no-restore-focus="noRestoreFocus"
      :location="location"
      :pointer="pointer"
      :permanent="isPermanent"
      :disabled="disabled"
      :anchor.prop="activator"
      @click.prevent.stop="onContentClick"
      @close="onSurfaceClosed"
    >
      <slot />
    </sp-popup>
  </div>
</template>

<script setup>
/**
 * The SpMenu component shows a menu at the position of the element used to activate it.
 *
 * @displayName Menu
 * @group Custom Elements
 * @component sp-menu
 *
 * @slot default - The menu's content.
 * @slot activator - The element that opens the menu when clicked.
 * @slot sheet-action - The bottom sheet's action, below the sm breakpoint. Defaults to a close
 *   button.
 */
import { onClickOutside, watchImmediate } from "@vueuse/core";
import { computed, nextTick, ref, toRef, useTemplateRef, watch } from "vue";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { useDisabled } from "#ce/composables/useDisabled";
import { t } from "#ce/shared/components-i18n";
import { toBoolean } from "#ce/utils/props";
import { props as menuProps } from "./props.js";

const emit = defineEmits(["input", "open", "close", "update-open"]);

const props = defineProps(menuProps);

const isActive = ref(toBoolean(props.open));
const isPermanent = ref(toBoolean(props.permanent));

watch(isActive, (value) => {
  emit("update-open", value);
});

watchImmediate(
  () => [props.open, props.value],
  () => (isActive.value = toBoolean(props.open)),
);

const closeOnContentClick = computed(() => toBoolean(props.closeOnContentClick));
const closeOnOutsideClick = computed(() => toBoolean(props.closeOnOutsideClick));
const { disabled } = useDisabled(toRef(props, "disabled"));

const container = useTemplateRef("container");

const { isGtSmScreen, ltModifiers } = useBreakpoints();

const containerModifierClass = computed(() => [...ltModifiers.value]);

async function onSurfaceClosed() {
  isActive.value = false;
  // The native surface is closed; let its closed-state classes render before handoff.
  await nextTick();
  if (!isActive.value) {
    emit("close");
  }
}

function onActivatorClick() {
  // sp-menu is not form-associated, so it must enforce its own disabled state —
  // there is no native :disabled suppression to fall back on.
  if (disabled.value) {
    return;
  }

  isActive.value = !isActive.value;
}

function onContentClick() {
  if (closeOnContentClick.value && isActive) {
    isActive.value = false;
  }
}

const activator = useTemplateRef("activator");

let clickOutsideStop;
const showBottomSheet = computed(() => !isPermanent.value && !isGtSmScreen.value);

watch(isActive, (value) => {
  if (!closeOnOutsideClick.value || showBottomSheet.value) {
    return;
  }
  if (value) {
    clickOutsideStop = onClickOutside(container, () => (isActive.value = false));
  } else if (clickOutsideStop) {
    clickOutsideStop();
  }
});

function onKeyDownEscape() {
  if (showBottomSheet.value) {
    return;
  }
  isActive.value = false;
}
</script>

<style>
:host {
  display: block;
  width: 100%;
}

:host([permanent]) {
  height: 100%;
}
</style>

<style scoped lang="scss">
.menu {
  position: relative;

  :host([permanent]) & {
    height: 100%;
  }
}
.menu-activator {
  cursor: pointer;
  display: block;
  position: relative;
}
sp-bottom-sheet {
  --bottom-sheet-border-radius: 8px 8px 0 0;
  --sp-comp-bottom-sheet-content-padding-inline: 0;
  --sp-comp-bottom-sheet-content-padding-block: 0.5rem;
  --sp-comp-bottom-sheet-action-container-padding-inline: 0.5rem;
}
.bottom-sheet-close-action {
  --button-width: 100%;
}
</style>
